import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Pie } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
} from 'chart.js';
import Button from 'components/Button/Button';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import { colorPalette, maIroScoringColors } from 'constants/colors';
import { BUTTON_VARIANT } from 'constants/interfaces';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IChartTopicRefinement from './interfaces/IChartTopicRefinement';

import styles from './scss/ChartTopicRefinement.module.scss';

ChartJS.register(ArcElement, Tooltip, Legend);

function ChartTopicRefinement({
  chartItems,
  selectedItemId,
  setSelectedItemId,
  handleBackToMainChart,
  vcOptions,
  valueSelector,
  setValueSelector,
}: Readonly<IChartTopicRefinement>) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [options, setOptions] = useState<IOption[]>([]);
  const [forceUpdate, setForceUpdate] = useState(0);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (vcOptions.length > 0) {
      setOptions([{ label: 'All', value: '' }, ...vcOptions]);
    }
  }, [vcOptions]);

  useEffect(() => {
    if (chartItems.length > 0) {
      const timer = setTimeout(() => {
        setForceUpdate((prev) => prev + 1);
        if (chartRef.current) {
          chartRef.current.update('none');
        }
      }, 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [chartItems]);

  const filteredItems = useMemo(() => {
    if (!value) return chartItems;
    return chartItems.filter((item) => item.title.toLowerCase().includes(value.toLowerCase()));
  }, [chartItems, value]);

  const chartDataAndPlugin = useMemo(() => {
    if (!chartItems || chartItems.length === 0) {
      return {
        data: {
          labels: [],
          datasets: [{
            data: [],
            backgroundColor: [],
            borderWidth: 0,
            hoverBorderWidth: 2,
            hoverBorderColor: maIroScoringColors.labelTextWhite,
            radius: '80%',
          }],
        },
        plugin: { id: 'percentagePlugin', afterDraw: () => {} },
      };
    }

    const data = {
      labels: chartItems.map((item) => item.title),
      datasets: [
        {
          data: chartItems.map((item) => item?.percentage),
          backgroundColor: chartItems.map((_, index) => colorPalette[index % colorPalette.length]),
          borderWidth: 0,
          hoverBorderWidth: 2,
          hoverBorderColor: maIroScoringColors.labelTextWhite,
          radius: '80%',
        },
      ],
    };

    const plugin = {
      id: 'percentagePlugin',
      afterDraw: (chart: any) => {
        const { ctx, chartArea } = chart;
        const meta = chart.getDatasetMeta(0);
        if (
          !ctx || !chartArea || !meta || !meta.data || meta.data.length === 0
          || !chartItems || chartItems.length === 0
        ) {
          return;
        }
        const centerX = chartArea.left + chartArea.width / 2;
        const centerY = chartArea.top + chartArea.height / 2;
        for (let i = 0; i < meta.data.length; i += 1) {
          const arc = meta.data[i];
          const item = chartItems[i];

          if (!arc || !item || typeof item.percentage === 'undefined' || item.percentage === null) {
            // eslint-disable-next-line no-continue
            continue;
          }

          const midAngle = (arc.startAngle + arc.endAngle) / 2;
          const innerRadius = arc.outerRadius * 1.02;
          const outerRadius = arc.outerRadius * 1.12;

          const x1 = centerX + Math.cos(midAngle) * innerRadius;
          const y1 = centerY + Math.sin(midAngle) * innerRadius;
          const x2 = centerX + Math.cos(midAngle) * outerRadius;
          const y2 = centerY + Math.sin(midAngle) * outerRadius;

          const isRightSide = Math.cos(midAngle) > 0;
          const labelX = x2 + (isRightSide ? 5 : -5);
          const labelY = y2;

          ctx.save();
          ctx.strokeStyle = maIroScoringColors.strokeStyle;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(x2, y2);
          ctx.lineTo(labelX - (isRightSide ? 2 : -2), labelY);
          ctx.stroke();
          ctx.fillStyle = maIroScoringColors.strokeStyle;
          ctx.font = 'bold 14px sans-serif';
          ctx.textAlign = isRightSide ? 'left' : 'right';
          ctx.textBaseline = 'middle';
          const text = `${item.percentage}%`;
          ctx.fillText(text, labelX, labelY);
          ctx.restore();
        }
      },
    };

    return { data, plugin };
  }, [chartItems]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const item = chartItems[context.dataIndex];
            return `${item.title}: ${item?.percentage}%`;
          },
        },
      },
    },
    onClick: (event: any, elements: any[]) => {
      if (selectedItemId !== null) {
        return;
      }
      if (elements.length > 0) {
        const elementIndex = elements[0].index;
        const clickedItem = chartItems[elementIndex];
        if (selectedItemId === clickedItem.topicId) {
          setSelectedItemId(null);
        } else {
          setSelectedItemId(clickedItem.topicId);
        }
      }
    },
  }), [chartItems, selectedItemId, setSelectedItemId]);

  const handleLegendItemClick = (topicId: number) => {
    if (selectedItemId !== null) {
      return;
    }
    if (selectedItemId === topicId) {
      setSelectedItemId(null);
    } else {
      setSelectedItemId(topicId);
    }
  };

  const onSearchChange = (search: string) => {
    setValue(search);
  };

  return (
    <div className={styles.root}>
      <div className={styles.chartWrapper}>
        <div className={styles.buttonContainer}>
          {selectedItemId ? (
            <Button
              title={t('materialityAssessment.backToMainChart')}
              onClick={handleBackToMainChart}
              variant={BUTTON_VARIANT.ghost}
              startIcon={<ArrowLeftIcon />}
            />
          ) : null}
        </div>
        <div className={styles.chartContainer}>
          {chartDataAndPlugin.data.labels.length === 0 ? (
            <EmptyTaskManagement
              title={t('materialityAssessment.chartIsEmpty')}
              checkIcon
            />
          ) : null}
          {chartDataAndPlugin.data.labels.length > 0 ? (
            <Pie
              ref={chartRef}
              data={chartDataAndPlugin.data}
              options={chartOptions}
              plugins={[chartDataAndPlugin.plugin]}
              key={forceUpdate}
            />
          ) : null}
        </div>
      </div>
      <div className={styles.legendWrapper}>
        <div className={styles.searchWrapper}>
          <TextField
            name="searchChartTopicRefinement"
            value={value}
            placeholder={t('common.searchByName')}
            icon={<SearchIcon />}
            onChange={(event) => onSearchChange(event.target.value)}
            required
          />
          <Select
            placeholder={t('common.selectVC')}
            options={options}
            value={options.find((option) => option.value === valueSelector) || null}
            onChange={(selectedOption) => setValueSelector((selectedOption as IOption)?.value || '')}
            menuPosition="absolute"
            maxMenuHeight={200}
          />
        </div>
        <div className={styles.legendsList}>
          {filteredItems.map((item, index) => (
            <button
              key={item.topicId}
              type="button"
              className={styles.legendItem}
              onClick={() => handleLegendItemClick(item.topicId)}
              aria-label={`Select ${item.title} category`}
            >
              <div
                className={styles.colorIndicator}
                style={{ backgroundColor: colorPalette[index % colorPalette.length] }}
              />
              <span className={styles.legendText}>
                {item.title}
              </span>
              <span className={styles.legendPercentage}>
                {item?.percentage}
                %
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ChartTopicRefinement;
